import { useEffect, useState } from 'react';
import { primaryNav } from '@/constants/siteNav';

export default function SiteNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState('world');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const ids = primaryNav.map((item) => item.href.replace('#', ''));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => Boolean(el));
    if (sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
    );
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const light = !scrolled;

  const linkClass = (key: string) =>
    [
      'px-3 py-2 text-[13px] tracking-[0.14em] uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap',
      light
        ? 'text-background-50/85 hover:text-background-50'
        : 'text-foreground-600 hover:text-primary-600',
      active === key ? (light ? 'text-background-50' : 'text-primary-600') : '',
    ].join(' ');

  return (
    <header
      className={[
        'fixed inset-x-0 top-0 z-50 transition-all duration-500',
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent',
      ].join(' ')}
    >
      <div className="flex h-16 md:h-20 w-full items-center justify-between px-4 md:px-10">
        <a
          href="#top"
          className="flex items-center gap-2.5 cursor-pointer"
          aria-label="NOMOISM 首页"
        >
          <span
            className={[
              'flex h-8 w-8 items-center justify-center rounded-full border transition-colors duration-300',
              light ? 'border-background-50/60' : 'border-primary-300',
            ].join(' ')}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/a35a200c-ca7e-438d-9729-841885b10129.png"
              alt="NOMOISM 品牌标识"
              className="h-5 w-5 object-contain"
            />
          </span>
          <span
            className={[
              'font-heading text-lg md:text-xl tracking-[0.28em] transition-colors duration-300',
              light ? 'text-background-50' : 'text-foreground-950',
            ].join(' ')}
          >
            NOMOISM
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {primaryNav.map((item) => (
            <a key={item.key} href={item.href} className={linkClass(item.key)}>
              {item.en}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-1">
          <a
            href="#contact"
            className={[
              'inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs tracking-[0.16em] uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap',
              light
                ? 'bg-background-50 text-foreground-900 hover:bg-primary-500 hover:text-background-50'
                : 'bg-primary-500 text-background-50 hover:bg-primary-600',
            ].join(' ')}
          >
            合作联系
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen(true)}
          aria-label="打开菜单"
          className={[
            'lg:hidden flex h-10 w-10 items-center justify-center rounded-full cursor-pointer',
            light ? 'text-background-50' : 'text-foreground-900',
          ].join(' ')}
        >
          <i className="ri-menu-line text-2xl"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-background-50">
          <div className="flex h-16 items-center justify-between px-4">
            <span className="font-heading text-lg tracking-[0.28em] text-foreground-950">
              NOMOISM
            </span>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label="关闭菜单"
              className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-900 cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <nav className="flex flex-col px-4 pt-6">
            {primaryNav.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={() => setMenuOpen(false)}
                className="flex items-baseline justify-between border-b border-background-200/70 py-4 cursor-pointer"
              >
                <span className="font-heading text-xl text-foreground-950">{item.zh}</span>
                <span className="text-xs tracking-[0.2em] text-foreground-400">{item.en}</span>
              </a>
            ))}
            <div className="mt-8 border-t border-background-200/70 pt-6">
              <a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                className="group flex w-full items-center justify-between gap-3 rounded-full bg-primary-500 px-6 py-4 text-sm tracking-[0.12em] text-background-50 transition-colors duration-300 hover:bg-primary-600 cursor-pointer whitespace-nowrap"
              >
                <span className="flex items-center gap-2.5">
                  <i className="ri-mail-send-line text-lg"></i>
                  品牌合作 · 联系我们
                </span>
                <i className="ri-arrow-right-line text-lg transition-transform duration-300 group-hover:translate-x-1"></i>
              </a>
              <p className="mt-3 text-center text-xs tracking-[0.04em] text-foreground-500">
                商务合作 / 媒体采访 / IP 授权，欢迎来信
              </p>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}